import React, { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { useTranslation } from "react-i18next";

interface PinDialogProps {
  onClose: () => void;
  /** Pins the post; `requiresAck` asks everybody to press "Susipažinau". Reports a failed request itself. */
  onPin: (requiresAck: boolean) => Promise<void>;
}

/** Shown while a post is being pinned. It is mounted only while open, so the box starts off every time. */
export function PinDialog({ onClose, onPin }: PinDialogProps) {
  const { t } = useTranslation();
  const [requiresAck, setRequiresAck] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const pin = async () => {
    setIsSaving(true);
    try {
      await onPin(requiresAck);
    } finally {
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
          <DialogTitle className="text-lg font-semibold text-gray-900">{t("news_pin_dialog_title")}</DialogTitle>
          <label className="mt-4 flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={requiresAck}
              onChange={(e) => setRequiresAck(e.target.checked)}
              disabled={isSaving}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">{t("news_pin_requires_ack")}</span>
          </label>
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="rounded-lg px-4 py-2 font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              {t("feed_cancel")}
            </button>
            <button
              type="button"
              onClick={pin}
              disabled={isSaving}
              className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {t("news_pin_action")}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
